import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState, AppDispatch } from "../../../../../store/store";
import { fetchStockListAsync, deleteStock, approveStockAsync } from "../../../../../store/Slice/stockSlice";
import debounce from "lodash/debounce";
import { FadeLoader } from "react-spinners";
import { toast } from "react-toastify";
import { IoEyeOutline } from "react-icons/io5";
import { BsDownload } from "react-icons/bs";
import { RiDeleteBinLine } from "react-icons/ri";
import { MdOutlinePendingActions } from "react-icons/md";
import { FiEdit } from "react-icons/fi";
import StockEditForm from "./StockEditForm";
import StockDetails from "./StockDetails";
import { downloadStockPDF } from "./stockDownload";

type ListParams = { page?: number; limit?: number; search?: string; fromDate?: string; toDate?: string };

type GeneratedBy = { username?: string; email?: string };
type Instrument = { name?: string };
type StockItem = {
  _id: string;
  orderNumber?: string;
  instrument?: Instrument | null;
  generatedBy?: GeneratedBy | null;
  responsiblePerson?: string | null;
  approvedBy?: string | null;
  createdAt?: string | null;
  status?: "pending" | "completed" | "rejected";
  approvals?: {
    production?: boolean;
    soldering?: boolean;
    store?: boolean;
  };
};

type DebouncedWithCancel = ((params: ListParams) => void) & { cancel?: () => void };

const DashboardStockList: React.FC<{ refreshTrigger?: boolean }> = ({ refreshTrigger }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { items = [], total = 0, status, error } = useSelector((s: RootState) => s.stock);
  const loading = status === "loading";
  const { user } = useSelector((state: RootState) => state.auth);

  const [searchQuery, setSearchQuery] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [dateErrors, setDateErrors] = useState({ fromDate: "", toDate: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const [showDetails, setShowDetails] = useState(false);
  const [selectedStock, setSelectedStock] = useState<StockItem | null>(null);
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editStock, setEditStock] = useState<StockItem | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [stockToDelete, setStockToDelete] = useState<string | null>(null);

  const today = new Date().toISOString().split("T")[0];

  const validateDates = (field: "fromDate" | "toDate", value: string) => {
    const dateValue = new Date(value);
    const todayDate = new Date(today);

    if (dateValue > todayDate) {
      setDateErrors((prev) => ({
        ...prev,
        [field]: "Date cannot be in the future.",
      }));
    } else {
      setDateErrors((prev) => ({ ...prev, [field]: "" }));
    }

    if (fromDate && toDate) {
      if (new Date(fromDate) > new Date(toDate)) {
        setDateErrors((prev) => ({
          ...prev,
          fromDate: "From Date cannot be after To Date.",
          toDate: "To Date cannot be before From Date.",
        }));
      } else {
        setDateErrors((prev) => ({ ...prev, fromDate: "", toDate: "" }));
      }
    }
  };

  const debouncedFetch = useMemo(() =>
    debounce((params: ListParams) => {
      dispatch(fetchStockListAsync(params));
    }, 400),
    [dispatch]
  ) as DebouncedWithCancel;

  useEffect(() => {
    const params: ListParams = {
      search: searchQuery || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      page: currentPage,
      limit: rowsPerPage,
    };
    debouncedFetch(params);
    return () => {
      if (debouncedFetch.cancel) debouncedFetch.cancel();
    };
  }, [searchQuery, fromDate, toDate, currentPage, refreshTrigger, debouncedFetch]);

  const handleView = (item: StockItem) => {
    setSelectedStock(item);
    setShowDetails(true);
  };

  const handleEdit = (item: StockItem) => {
    setEditStock(item);
    setShowEditModal(true);
  };

  const confirmDelete = async (): Promise<void> => {
    if (stockToDelete) {
      try {
        await dispatch(deleteStock(stockToDelete)).unwrap();
        toast.error("Stock moved to Recycle Bin", {
          toastId: "stock-deleted",
        });
        dispatch(fetchStockListAsync({ page: currentPage, limit: rowsPerPage }));
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        toast.error(msg || "Delete failed");
      }
      setShowAlert(false);
      setStockToDelete(null);
    }
  };

  const APPROVAL_DEPARTMENTS = ["production", "soldering", "store"];

  const canApproveStock = (stock: StockItem) => {
    if (!user) return false;

    // Only admin/subadmin
    if (!["admin", "subadmin"].includes(user.userType)) return false;

    // Only specific departments
    if (!APPROVAL_DEPARTMENTS.includes(user.department!)) return false;

    // Already completed
    if (stock.status === "completed") return false;

    const dept = user.department as "production" | "soldering" | "store";

    // Already approved by this department
    if (stock.approvals?.[dept]) return false;

    return true;
  };


  const handleApprove = async (stockId: string) => {
    try {
      await dispatch(approveStockAsync(stockId)).unwrap();
      toast.success("Stock approved successfully");
    } catch (err: any) {
      toast.error(err?.message || "Approval failed");
    }
  };


  const handleDownload = (s: StockItem) => {
    const pdfData = {
      stockNumber: s.orderNumber,
      stocks: [
        {
          id: 1,
          instrument: {
            name: s.instrument?.name || "N/A",
            code: "", // list me nahi hai
          },
          parameters: "",
          quantity: 1,
          responsiblePerson: s.responsiblePerson || "N/A",
          approvedBy: s.approvedBy || "N/A",
          remark: "",
        },
      ],
    };

    downloadStockPDF(pdfData);
  };


  const totalPages = Math.ceil(total / rowsPerPage);

  if (loading) {
    return (
      <div className="p-5 text-center text-gray-600 dark:text-gray-300">
        <FadeLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 text-center text-red-600">
        Error loading stock: {String(error)}
        <p className="text-sm text-gray-500 mt-2">
          Please try logging in again or check your network connection.
        </p>
      </div>
    );
  }

  return (
    <div className="border-2 border-gray-300 rounded-lg p-5 mt-10 overflow-x-auto lg:text-lg md:text-sm text-xs mb-20">
      {/* Delete Confirmation Modal */}
      {showAlert && (
        <div className="fixed inset-0 flex items-center justify-center p-4 backdrop-filter backdrop-blur-md z-10">
          <div className="bg-red-100 p-6 rounded-lg shadow-xl max-w-sm w-full">
            <h3 className="text-xl font-bold text-red-600 mb-4">
              Confirm Deletion
            </h3>
            <p className="mb-4 text-gray-700">
              Are you sure you want to delete this stock request?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowAlert(false);
                  setStockToDelete(null);
                }}
                className="bg-white text-gray-800 font-bold py-2 px-4 rounded hover:bg-gray-100 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="bg-red-600 text-white font-bold py-2 px-4 rounded hover:bg-red-700 transition-colors cursor-pointer"
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header Filters */}
      <div className="flex justify-between lg:flex-row xl:flex-row flex-col items-start mb-4">
        <div>
          <p className="font-semibold lg:text-lg xl:text-xl text-sm text-gray-800 dark:text-white">
            Recent Stock Requests
          </p>
        </div>
        <div className="flex items-end gap-4 xl:flex-row lg:flex-row flex-col xl:text-sm lg:text-sm text-xs mt-2">
          <span className="search border-2 bg-white dark:bg-zinc-800 w-full flex items-center justify-center rounded-lg">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="p-2 size-10"
            >
              <path
                fillRule="evenodd"
                d="M10.5 3.75a6.75 6.75 0 1 0 0 13.5 6.75 6.75 0 0 0 0-13.5ZM2.25 10.5a8.25 8.25 0 1 1 14.59 5.28l4.69 4.69a.75.75 0 1 1-1.06 1.06l-4.69-4.69A8.25 8.25 0 0 1 2.25 10.5Z"
                clipRule="evenodd"
              />
            </svg>
            <input
              id="search"
              name="search"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              type="search"
              placeholder="Search Stock..."
              className="w-full p-2 font-semibold lg:text-sm text-xs"
            />
          </span>

          <div className="flex flex-row md:flex-row gap-4 w-full">
            {/* From Date */}
            <div className="flex flex-col w-full md:w-1/2">
              <label
                htmlFor="from-date"
                className="text-xs text-gray-700 dark:text-white font-medium mb-1"
              >
                From Date
              </label>
              <input
                type="date"
                id="from-date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  validateDates("fromDate", e.target.value);
                  setCurrentPage(1);
                }}
                className={`p-2 border bg-white dark:bg-zinc-800 ${dateErrors.fromDate
                  ? "border-red-500"
                  : "border-gray-300 dark:border-zinc-600"
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                max={today}
              />
              {dateErrors.fromDate && (
                <span className="text-xs text-red-500 mt-1">
                  {dateErrors.fromDate}
                </span>
              )}
            </div>

            {/* To Date */}
            <div className="flex flex-col w-full md:w-1/2">
              <label
                htmlFor="to-date"
                className="text-xs text-gray-700 dark:text-white font-medium mb-1"
              >
                To Date
              </label>
              <input
                type="date"
                id="to-date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  validateDates("toDate", e.target.value);
                  setCurrentPage(1);
                }}
                className={`p-2 border bg-white dark:bg-zinc-800 ${dateErrors.toDate
                  ? "border-red-500"
                  : "border-gray-300 dark:border-zinc-600"
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                max={today}
              />
              {dateErrors.toDate && (
                <span className="text-xs text-red-500 mt-1">
                  {dateErrors.toDate}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full">
        {/* Desktop Table View */}
        <div className="hidden lg:block">
          <table className="w-full text-xs mb-4">
            <thead>
              <tr className="bg-gray-200 dark:bg-zinc-950 text-xs">
                <th className="p-2 text-xl text-start">Order Number</th>
                <th className="p-2 text-xl text-start">Generated By</th>
                <th className="p-2 text-xl text-start">Instrument</th>
                <th className="p-2 text-xl">Created Date</th>
                <th className="p-2 text-xl">Responsible</th>
                <th className="p-2 text-xl">Approved By</th>
                <th className="p-2 text-xl">Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(items) && items.length > 0 ? (
                (items as StockItem[]).map((s: StockItem) => (
                  <tr
                    key={s._id}
                    className="border-b border-gray-200 dark:border-zinc-600 odd:bg-white dark:odd:bg-zinc-800 even:bg-gray-50 even:dark:bg-zinc-900"
                  >
                    <td className="text-start">
                      <span className="p-2 text-blue-800 dark:text-blue-400 font-bold hover:underline text-sm">
                        {s.orderNumber}
                      </span>
                    </td>
                    <td className="p-2 flex items-center gap-3">
                      <div className="flex flex-col">
                        <span className="text-start font-semibold text-sm uppercase">
                          {s.generatedBy?.username || "N/A"}
                        </span>
                        <span className="text-start text-xs">
                          {s.generatedBy?.email || "N/A"}
                        </span>
                      </div>
                    </td>
                    <td className="lg:p-2 p-1 text-start">
                      <span className="font-semibold text-sm">
                        {s.instrument?.name || "N/A"}
                      </span>
                    </td>
                    <td className="lg:p-2 p-1 font-semibold text-sm">
                      {s.createdAt ? new Date(s.createdAt).toLocaleDateString() : "N/A"}
                    </td>
                    <td className="lg:p-2 p-1 font-semibold text-sm">
                      {s.responsiblePerson || "N/A"}
                    </td>
                    <td className="lg:p-2 p-1 font-semibold text-sm">
                      {s.approvedBy || "N/A"}
                    </td>
                    <td className="lg:p-2 p-1 lg:gap-3 gap-1 lg:text-3xl text-lg flex justify-center items-center pr-5">
                      <div className="relative group inline-block">

                        {/* MAIN BUTTON */}
                        <button className="px-3 py-1  shadow-sm  text-sm">
                          …
                        </button>

                        {/* DROPDOWN */}
                        <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2
      opacity-0 invisible group-hover:opacity-100 group-hover:visible
      transition-all duration-200 z-50
      bg-white dark:bg-zinc-900 border shadow-lg rounded-lg p-2 w-40">
                          {/* View */}
                          <div
                            className="flex items-center gap-2 p-2 rounded hover:bg-gray-100 dark:hover:bg-zinc-800 cursor-pointer"
                            onClick={() => handleView(s)}
                          >
                            <IoEyeOutline className="text-lg" />
                            <span className="text-sm">View</span>
                          </div>

                          {/* Download */}
                          <div
                            className="flex items-center gap-2 p-2 rounded hover:bg-gray-100 dark:hover:bg-zinc-800 cursor-pointer"
                            onClick={() => handleDownload(s)}
                          >
                            <BsDownload className="text-lg" />
                            <span className="text-sm">Download</span>
                          </div>

                          {/* Edit */}
                          <div
                            className="flex items-center gap-2 p-2 rounded hover:bg-gray-100 dark:hover:bg-zinc-800 cursor-pointer"
                            onClick={() => handleEdit(s)}
                          >
                            <FiEdit className="text-lg" />
                            <span className="text-sm">Edit</span>
                          </div>

                          {/* Delete */}
                          {(user?.userType === "admin" || user?.userType === "subadmin") && (
                            <div
                              className="flex items-center gap-2 p-2 rounded hover:bg-gray-100 dark:hover:bg-zinc-800 cursor-pointer text-red-500"
                              onClick={() => {
                                setStockToDelete(s._id);
                                setShowAlert(true);
                              }}
                            >
                              <RiDeleteBinLine className="text-lg" />
                              <span className="text-sm">Delete</span>
                            </div>
                          )}

                          {/* Approve */}
                          {canApproveStock(s) && (
                            <div
                              className="flex items-center gap-2 p-2 rounded hover:bg-gray-100 dark:hover:bg-zinc-800 cursor-pointer"
                              onClick={() => handleApprove(s._id)}
                            >
                              <MdOutlinePendingActions className="text-lg text-yellow-600" />
                              <span className="text-sm">Approve</span>
                            </div>
                          )}

                          {/* Approved badge */}
                          {user &&
                            ["production", "soldering", "store"].includes(user.department!) &&
                            s.approvals?.[user.department as "production" | "soldering" | "store"] && (
                              <div className="flex items-center gap-2 p-2 rounded cursor-default text-green-600">
                                <span className="text-lg">✅</span>
                                <span className="text-sm">Approved</span>
                              </div>
                            )}
                        </div>
                      </div>

                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center p-4 text-gray-500">
                    No stock found for the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden space-y-4">
          {Array.isArray(items) && (items as StockItem[]).length > 0 ? (
            (items as StockItem[]).map((s: StockItem) => (
              <div
                key={s._id}
                className="border border-gray-300 rounded-md text-xs"
              >
                <div className="flex border-b p-2">
                  <span className="w-1/3 font-semibold text-gray-600 dark:text-gray-300 text-start">
                    Order Number:
                  </span>
                  <span className="w-2/3 text-blue-800 dark:text-blue-500 font-bold text-left">
                    {s.orderNumber}
                  </span>
                </div>

                <div className="flex border-b p-2 items-center gap-2">
                  <span className="w-1/3 font-semibold text-gray-600 dark:text-gray-300 text-start">
                    Generated By:
                  </span>
                  <div className="w-2/3 flex gap-2 items-center text-left">
                    <div className="text-left">
                      <p>{s.generatedBy?.username || "N/A"}</p>
                      <p className="text-gray-500 dark:text-gray-300 text-xs">
                        {s.generatedBy?.email}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex border-b p-2">
                  <span className="w-1/3 font-semibold text-gray-600 dark:text-gray-300 text-start">
                    Instrument:
                  </span>
                  <span className="w-2/3 text-left font-semibold">
                    {s.instrument?.name || "N/A"}
                  </span>
                </div>

                <div className="flex border-b p-2">
                  <span className="w-1/3 font-semibold text-gray-600 dark:text-gray-300 text-start">
                    Created:
                  </span>
                  <span className="w-2/3 text-left">
                    {s.createdAt ? new Date(s.createdAt).toLocaleDateString() : "N/A"}
                  </span>
                </div>

                <div className="flex border-b p-2">
                  <span className="w-1/3 font-semibold text-gray-600 dark:text-gray-300 text-start">
                    Responsible:
                  </span>
                  <span className="w-2/3 text-left">{s.responsiblePerson || "N/A"}</span>
                </div>

                <div className="flex border-b p-2">
                  <span className="w-1/3 font-semibold text-gray-600 dark:text-gray-300 text-start">
                    Approved By:
                  </span>
                  <span className="w-2/3 text-left">{s.approvedBy || "N/A"}</span>
                </div>

                <div className="flex justify-end gap-4 p-2 text-xl">
                  <IoEyeOutline
                    onClick={() => handleView(s)}
                    className="hover:bg-blue-800 p-1 rounded-sm hover:text-white duration-200 cursor-pointer"
                  />
                  <BsDownload
                    onClick={() => handleDownload(s)}
                    className="hover:bg-blue-800 p-1 rounded-sm hover:text-white duration-200 cursor-pointer"
                  />
                  <FiEdit
                    onClick={() => handleEdit(s)}
                    className="hover:bg-blue-800 p-1 rounded-sm hover:text-white duration-200 cursor-pointer"
                  />

                  {canApproveStock(s) && (
                    <button
                      onClick={() => handleApprove(s._id)}
                      className="px-3 py-1 text-xs bg-green-600 text-white rounded"
                    >
                      Approve
                    </button>
                  )}

                  {user &&
                    ["production", "soldering", "store"].includes(user.department!) &&
                    s.approvals?.[user.department as "production" | "soldering" | "store"] && (
                      <span className="px-3 py-1 text-xs bg-green-700 text-white rounded flex items-center gap-1">
                        ✅ Approved
                      </span>
                    )}


                  {(user?.userType === "admin" || user?.userType === "subadmin") && (
                    <RiDeleteBinLine
                      onClick={() => {
                        setStockToDelete(s._id);
                        setShowAlert(true);
                      }}
                      className="text-red-500 hover:bg-blue-800 p-1 rounded-sm duration-200 cursor-pointer"
                    />
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center p-4 text-gray-500">
              No stock found for the current filters.
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-4">
            {/* Prev Button */}
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-2 py-1 rounded bg-gray-200 dark:bg-zinc-900 text-sm hover:bg-blue-200 disabled:opacity-50"
            >
              ←
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => {
                if (p === 1 || p === 2 || p === totalPages || p === totalPages - 1) return true;
                if (p === currentPage) return true;
                return false;
              })
              .map((p, i, arr) => {
                const prevPage = arr[i - 1];
                return (
                  <span key={p} className="flex items-center gap-2">
                    {prevPage && p - prevPage > 1 && <span className="px-2">...</span>}
                    <button
                      onClick={() => setCurrentPage(p)}
                      className={`px-3 py-1 rounded-full text-sm ${currentPage === p
                        ? "bg-blue-600 text-white font-semibold"
                        : "bg-gray-100 dark:bg-[var(--theme-color)] hover:bg-blue-100"
                        }`}
                    >
                      {p}
                    </button>
                  </span>
                );
              })}

            {/* Next Button */}
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-2 py-1 rounded bg-gray-200 dark:bg-zinc-900 text-sm hover:bg-blue-200 disabled:opacity-50"
            >
              →
            </button>
          </div>
        )}
      </div>

      {/* View Modal */}
      {showDetails && selectedStock && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div className="relative">
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 text-2xl font-bold z-10"
              onClick={() => setShowDetails(false)}
              aria-label="Close"
            >
              &times;
            </button>
            <StockDetails
              orderData={selectedStock}
              onClose={() => setShowDetails(false)}
            />
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editStock && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div className="relative">
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 text-2xl font-bold z-10"
              onClick={() => setShowEditModal(false)}
              aria-label="Close"
            >
              &times;
            </button>
            <StockEditForm
              stock={editStock}
              onClose={() => setShowEditModal(false)}
              onSaved={() => dispatch(fetchStockListAsync({ page: currentPage, limit: rowsPerPage }))}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardStockList;